import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Message } from 'semantic-ui-react';
import {
  Field,
  reduxForm,
  formValueSelector,
  SubmissionError,
  change,
} from 'redux-form';
import {
  showEditPriceModal,
  updateAddonProductPrice,
} from 'state/additionalService/actions';
import ValidatedFormInput from 'components/ValidatedFormInput';
import {
  requiredFieldValidation,
  optionalDiscountPercentageValidation,
} from 'utils/formUtils';
import PriceBreakdown from 'pages/BillingPackage/components/PriceBreakdown';
import ApprovalConfigSection from 'pages/BillingPackage/components/ApprovalConfigSection';

const validate = values => {
  const errors = {};

  const priceRequiredError = requiredFieldValidation(
    values.price_per_unit,
    'Price'
  );
  if (priceRequiredError) {
    errors.price_per_unit = priceRequiredError;
  } else if (Number(values.price_per_unit) <= 0) {
    errors.price_per_unit = 'Price must be greater than 0';
  }

  const discountError = optionalDiscountPercentageValidation(
    values.discount_percentage
  );
  if (discountError) {
    errors.discount_percentage = discountError;
  }

  return errors;
};

export class ModularProductEditorModal extends React.Component {
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClose = this.onClose.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      discountPercentageValue,
      approvalRequirementsValue,
    } = this.props;

    const currentApproval = Array.isArray(approvalRequirementsValue)
      ? approvalRequirementsValue
      : [];

    const discountBecameFalsy =
      prevProps.discountPercentageValue &&
      (!discountPercentageValue || Number(discountPercentageValue) === 0);
    if (discountBecameFalsy && currentApproval.includes('HIGHER_DISCOUNT')) {
      dispatch(
        change(
          'priceEditorForm',
          'approval_requirements',
          currentApproval.filter(t => t !== 'HIGHER_DISCOUNT')
        )
      );
    }
  }

  onClose() {
    const { dispatch, reset } = this.props;
    dispatch(showEditPriceModal(false, null));
    reset();
  }

  onSubmit(values) {
    const errors = validate(values);

    if (Object.keys(errors).length > 0) {
      throw new SubmissionError(errors);
    }

    const { dispatch, editingProduct } = this.props;
    const discountPercentage =
      values.discount_percentage === '' ||
      values.discount_percentage === null ||
      values.discount_percentage === undefined
        ? 0
        : Number(values.discount_percentage);

    const payload = {
      ...editingProduct,
      price_per_unit: parseFloat(values.price_per_unit),
      discount_percentage: discountPercentage,
      approval_requirements: Array.isArray(values.approval_requirements)
        ? values.approval_requirements
        : [],
    };

    const excludeKeys = ['tax', 'discount_rule', 'created_at', 'updated_at'];
    const cleanedPayload = Object.entries(payload)
      .filter(([key, value]) => value !== null && !excludeKeys.includes(key))
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    return dispatch(updateAddonProductPrice(editingProduct.id, cleanedPayload));
  }

  render() {
    const {
      isOpen,
      isLoading,
      errorMessage,
      handleSubmit,
      editingProduct,
      discountPercentageValue,
      pricePerUnitValue,
    } = this.props;

    const pricePerUnit =
      parseFloat(
        pricePerUnitValue != null
          ? pricePerUnitValue
          : editingProduct && editingProduct.price_per_unit
      ) || 0;
    const discountPercentage = parseFloat(discountPercentageValue) || 0;

    return (
      <Modal open={isOpen} onClose={this.onClose} size="small">
        <Modal.Header>
          <div>Edit Modular Product</div>
          <p
            style={{
              fontWeight: 'normal',
              fontSize: '0.875rem',
              color: '#666',
              margin: '0.25em 0 0',
            }}
          >
            Update pricing, discount, and approval configuration
          </p>
        </Modal.Header>
        <Modal.Content>
          {errorMessage && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{errorMessage}</p>
            </Message>
          )}
          <Form onSubmit={handleSubmit(this.onSubmit)} loading={isLoading}>
            <Form.Group widths="equal">
              <Form.Field>
                <label>Modular Product</label>
                <input
                  type="text"
                  value={(editingProduct && editingProduct.name) || ''}
                  disabled
                  readOnly
                />
              </Form.Field>
              <Form.Field>
                <label>Period</label>
                <input type="text" value="Monthly" disabled readOnly />
              </Form.Field>
            </Form.Group>

            <Form.Group widths="equal">
              <Form.Field>
                <label>
                  Price (before tax) <span style={{ color: 'red' }}>*</span>
                </label>
                <Field
                  name="price_per_unit"
                  component={ValidatedFormInput}
                  type="number"
                  step="1"
                  min="0"
                  placeholder="Enter price"
                />
              </Form.Field>
              <Form.Field>
                <label>Discount (%)</label>
                <Field
                  name="discount_percentage"
                  component={ValidatedFormInput}
                  type="number"
                  placeholder="0"
                />
              </Form.Field>
            </Form.Group>

            <PriceBreakdown
              basePrice={pricePerUnit}
              duration={1}
              discount={discountPercentage}
            />

            <ApprovalConfigSection
              discountPercentageValue={discountPercentageValue}
              showBonusSubscription={false}
            />
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={this.onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit(this.onSubmit)}
            loading={isLoading}
            disabled={isLoading}
          >
            Save Changes
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }
}

ModularProductEditorModal.propTypes = {
  dispatch: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  isLoading: PropTypes.bool,
  errorMessage: PropTypes.string,
  editingProduct: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  discountPercentageValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  approvalRequirementsValue: PropTypes.arrayOf(PropTypes.string),
  pricePerUnitValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ModularProductEditorModal.defaultProps = {
  isOpen: false,
  isLoading: false,
  errorMessage: null,
  editingProduct: null,
  discountPercentageValue: 0,
  approvalRequirementsValue: [],
  pricePerUnitValue: null,
};

const selector = formValueSelector('priceEditorForm');

const mapStateToProps = state => {
  const { priceEditor } = state.additionalService;
  const { editingProduct } = priceEditor;
  const discountFromRule =
    editingProduct &&
    editingProduct.discount_rule &&
    editingProduct.discount_rule.discount_percentage;

  return {
    isOpen: priceEditor.isOpen,
    isLoading: priceEditor.isLoading,
    errorMessage: priceEditor.errorMessage,
    editingProduct,
    initialValues: {
      price_per_unit: editingProduct && editingProduct.price_per_unit,
      discount_percentage: discountFromRule != null ? discountFromRule : 0,
      approval_requirements:
        (editingProduct && editingProduct.approval_requirements) || [],
    },
    discountPercentageValue: selector(state, 'discount_percentage'),
    approvalRequirementsValue: selector(state, 'approval_requirements'),
    pricePerUnitValue: selector(state, 'price_per_unit'),
  };
};

export default connect(mapStateToProps)(
  reduxForm({
    form: 'priceEditorForm',
    enableReinitialize: true,
  })(ModularProductEditorModal)
);
